import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, products, categoryFAQ, type ProductItem } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [filteredProducts, setFilteredProducts] = useState<ProductItem[]>([]);
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter((p) => p.categoryId === activeCategory));
    }
  }, [activeCategory]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeCategory]);

  const currentCat = categories.find((c) => c.id === activeCategory);
  const pageTitle = currentCat ? `${currentCat.name} - 开云官网` : '服务分类 - 开云官网';

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const faqs = categoryFAQ[activeCategory] || [];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '服务分类' },
            ]}
          />

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
              {currentCat?.name || '服务分类'}
            </h1>
            <p className="text-sm text-foreground-500">{currentCat?.description}</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' }); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14" data-product-shop>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.badge && (
                    <span className="absolute top-3 right-3 px-2.5 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-semibold">
                      {product.badge}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold font-heading text-foreground-900 mb-2">{product.name}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-3">{product.description}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {product.features.map((feat) => (
                      <span key={feat} className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 text-[10px] font-medium">
                        {feat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {activeCategory !== 'all' && faqs.length > 0 && (
            <div className="mb-14 max-w-2xl">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-5">常见问题</h2>
              <div className="space-y-3">
                {faqs.map((faq) => (
                  <div key={faq.id} className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
                    >
                      <span className="text-sm font-medium text-foreground-800">{faq.question}</span>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                        openFAQ === faq.id ? 'bg-primary-100 rotate-45' : 'bg-background-200'
                      }`}>
                        <i className={`ri-add-line text-xs ${openFAQ === faq.id ? 'text-primary-600' : 'text-foreground-400'}`}></i>
                      </div>
                    </button>
                    <div className={`transition-all duration-300 overflow-hidden ${
                      openFAQ === faq.id ? 'max-h-40 pb-4 px-4' : 'max-h-0'
                    }`}>
                      <p className="text-xs text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
              {faqs.length > 0 && (
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'FAQPage',
                      mainEntity: faqs.map((f) => ({
                        '@type': 'Question',
                        name: f.question,
                        acceptedAnswer: { '@type': 'Answer', text: f.answer },
                      })),
                    }),
                  }}
                />
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}